// ═══════════════════════════════════════════════════════════════════════════
// grainCurve.js — the FULL relevant futures curve for a crop year, not just
// the nearby board (Kevin, 2026-08-18: "stop anchoring the advisor to a
// single nearby board month... expose all relevant forward futures
// contracts/delivery periods available in the existing market data").
//
// grainAction.js's buildGrainAction() still evaluates ONE board per call (the
// crop year's new-crop contract) — that contract stays the ECONOMICS anchor
// (breakeven, required-remaining-price, before/after scenario all need ONE
// price to compare against). This file is additive: it takes each contract's
// ALREADY-COMPUTED grainContractTech() reading (the caller fetches
// futures_contract_history per contract and calls grainContractTech, exactly
// as buildGrainAction's caller already does for the single board) and
// produces one row per contract for a forward-curve VIEW — never flattened
// into one continuous series (Kevin: "preserve contract-specific evidence").
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { grainTargetLadder } from "../grainTargets";

// ── Categorical futures-opportunity gauge ───────────────────────────────────
// Same 3-way percentile labeling grainCash.js's basisHistoryContext() already
// uses for basis (>=67th "strong", <=33rd "weak", else "normal") — one
// methodology reused for a second gauge, not a second invented scale. No
// black-box weight: this is a direct read of tc.pctile, which grainAction.js
// already surfaces as "the share of this board's own life-to-date closes
// sitting at or below today's price."
export function opportunityCategory(pctile) {
  if (pctile == null) return "unknown";
  const p = pctile * 100;
  if (p >= 67) return "strong";
  if (p <= 33) return "weak";
  return "normal";
}

// contractBars: [{ sym, label, isNewCrop, tc }] — one entry per delivery
// contract in the crop year, in delivery order. `tc` is that contract's own
// grainContractTech() reading (or null when its board has < 30 bars — the
// same "thin history, market signal degraded" threshold buildGrainAction
// already applies to the single board). The caller computes tc per contract
// itself (grainContractTech is a pure function of that contract's own bars);
// this module only labels and orders what it's handed.
//
// regime reuses grainTargetLadder()'s own trend read (up/down against the
// 100-day, falling back to the 50-day) rather than re-deriving that formula a
// second time — curve/horizon are irrelevant to the regime field itself, so
// they're passed as null/a placeholder and never used for anything shown
// here (grainTargetLadder's rung placement is NOT part of this file's job;
// buildGrainAction still owns the one board's actual target ladder).
export function buildForwardCurve({ crop, cropYear, contractBars = [] }) {
  const points = (contractBars || []).map((c) => {
    const tc = c && c.tc ? c.tc : null;
    const regime = tc ? grainTargetLadder(tc, null, 126).regime : null;
    return {
      sym: c ? c.sym : null,
      label: c ? c.label : null,
      isNewCrop: !!(c && c.isNewCrop),
      hasData: !!tc,
      last: tc ? tc.last : null,
      pctile: tc ? tc.pctile : null,
      // Bar count, carried so a reader (and buildFuturesGauge) can tell how
      // much archive a life-to-date percentile is actually standing on.
      // Contracts on this board differ by an order of magnitude — measured
      // 2026-08-19: CZ26 780 bars vs CZ27 23 — and without `n` a 23-bar
      // contract's "100th percentile" is indistinguishable from a 780-bar
      // one's 83rd.
      n: tc ? tc.n : null,
      regime,
      opportunity: opportunityCategory(tc ? tc.pctile : null),
      asOf: tc ? tc.asOf : null,
    };
  });
  return { crop, cropYear, points };
}
